import { httpService } from '@services/httpService';
import { ApiRoute } from '@routes/apiRoutes';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@enums/queryKeys';
import { TableResponseModel } from '@models/table/tableResponseModel';
import { MessageResponseModel } from '@models/messages/messageResponseModel';
import { SendMessageRequestModel } from '@models/messages/sendMessageRequestModel';
import { DeleteMessageRequestModel } from '@models/messages/deleteMessageRequestModel';
import { TableRequestModel } from '@models/table/tableRequestModel';
import { QueryCacheConstants } from '@constants/queryCacheConstants';

export const useFetchInboxMessagesApi = (
  request: TableRequestModel<string>
) => {
  const getInboxMessages = async (request: TableRequestModel<string>) => {
    return await httpService.get<TableResponseModel<MessageResponseModel>>(
      ApiRoute.InboxMessages,
      request
    );
  };
  return useQuery({
    queryKey: [
      QueryKeys.INBOX_MESSAGES,
      request.params.page,
      request.params.rowsPerPage,
      request.params.order?.name,
      request.params.order?.direction,
    ],
    queryFn: () => getInboxMessages(request),
    staleTime: QueryCacheConstants.NO_CACHE,
    gcTime: QueryCacheConstants.DEFAULT_GC_TIME,
    refetchOnWindowFocus: true,
  });
};

export const useFetchSentMessagesApi = (request: TableRequestModel<string>) => {
  const getSentMessages = async (request: TableRequestModel<string>) => {
    const result = await httpService.get<
      TableResponseModel<MessageResponseModel>
    >(ApiRoute.SentMessages, request);
    return result;
  };

  const queryKey = [
    QueryKeys.SENT_MESSAGES,
    request.params.page,
    request.params.rowsPerPage,
    request.params.order?.name,
    request.params.order?.direction,
  ];

  return useQuery({
    queryKey,
    queryFn: () => getSentMessages(request),
    staleTime: QueryCacheConstants.NO_CACHE,
    gcTime: QueryCacheConstants.DEFAULT_GC_TIME,
  });
};

export const useSendMessageApi = () => {
  const sendMessage = async (message: SendMessageRequestModel) => {
    return await httpService.post<boolean>(ApiRoute.SendMessage, message);
  };
  return useMutation({
    mutationFn: (message: SendMessageRequestModel) => sendMessage(message),
  });
};

export const useDeleteMessageApi = () => {
  const queryClient = useQueryClient();
  const deleteMessage = async (message: DeleteMessageRequestModel) => {
    return await httpService.post<boolean>(ApiRoute.DeleteMessage, message);
  };
  return useMutation({
    mutationFn: (message: DeleteMessageRequestModel) => deleteMessage(message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) &&
          [
            QueryKeys.INBOX_MESSAGES,
            QueryKeys.SENT_MESSAGES,
            QueryKeys.UNREAD_MESSAGE_COUNT,
          ].includes(query.queryKey[0]),
      });
    },
  });
};

export const useUpdateReadDateTimeApi = () => {
  const queryClient = useQueryClient();
  const updateReadDateTime = async (recId: string, companyId: string) => {
    const result = await httpService.put<boolean>(
      `${ApiRoute.UpdateReadDateTime}?recId=${recId}&companyId=${companyId}`
    );
    return result;
  };
  return useMutation({
    mutationFn: async ({
      recId,
      companyId,
    }: {
      recId: string;
      companyId: string;
    }) => updateReadDateTime(recId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) &&
          [QueryKeys.INBOX_MESSAGES, QueryKeys.UNREAD_MESSAGE_COUNT].includes(
            query.queryKey[0]
          ),
      });
    },
  });
};

export const useFetchMessageCount = () => {
  const getMessageCount = async () => {
    return await httpService.get<number>(ApiRoute.UnreadMessageCount);
  };
  return useQuery({
    queryKey: [QueryKeys.UNREAD_MESSAGE_COUNT],
    queryFn: () => getMessageCount(),
  });
};
